import { useFormContext } from 'react-hook-form';
import { Calendar, ExternalLink, Eye, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InvitationFormValues } from '@/types/invitation-schema';
import { FAMILY_ROLE_LABELS, FamilyRole, type ItineraryItem } from '@/types/invitation';
import { useEvent } from '@/hooks/useEvent';
import { buildPayload, AdapterInvitation, AdapterEvent } from '@/invitation/payload/adapter';
import type { InvitationPayload } from '@/invitation/payload/types';
import InvitationV2 from '@/invitation/render/InvitationV2';
import { parseCalendarDate } from '@/lib/parseCalendarDate';

// Datos ficticios solo para la vista previa: muestran las secciones que dependen del
// invitado (pase, confirmación) o de datos aún no cargados (lista de regalos).
const SMOKE_GIFTS = [
  { nombre: 'Juego de sábanas de lino', precio: 480, descripcion: 'Tono arena, king size' },
  { nombre: 'Batería de cocina', precio: 950 },
  { nombre: 'Cena romántica para dos', precio: 300, descripcion: 'En nuestro restaurante favorito' },
];

function withPreviewSmoke(payload: InvitationPayload): InvitationPayload {
  const sugerencias =
    payload.regalos?.sugerencias && payload.regalos.sugerencias.length > 0
      ? payload.regalos.sugerencias
      : SMOKE_GIFTS;
  return {
    ...payload,
    invitado: payload.invitado ?? { nombre: 'Nombre de invitado especial', pases: 2 },
    regalos: { ...payload.regalos, sugerencias },
    interruptores: { ...payload.interruptores, listaRegalos: true },
  };
}

interface FamilyMember {
  id: string;
  name: string;
  role: string;
  display_order?: number;
}

interface Props {
  isPublished: boolean;
  publicSlug: string | null;
  familyMembers: FamilyMember[];
}

/** Mapea los valores del formulario a la vista de invitación que consume el adapter. */
const toAdapterInvitation = (
  values: InvitationFormValues,
  familyMembers: FamilyMember[],
): AdapterInvitation => ({
  template_id: values.template_id,
  religious_ceremony_enabled: values.religious_ceremony_enabled,
  religious_ceremony_name: values.religious_ceremony_name,
  religious_ceremony_address: values.religious_ceremony_address,
  religious_ceremony_time: values.religious_ceremony_time,
  religious_ceremony_map_url: values.religious_ceremony_map_url,
  civil_ceremony_enabled: values.civil_ceremony_enabled,
  civil_ceremony_name: values.civil_ceremony_name,
  civil_ceremony_address: values.civil_ceremony_address,
  civil_ceremony_time: values.civil_ceremony_time,
  civil_ceremony_map_url: values.civil_ceremony_map_url,
  party_name: values.party_name,
  party_address: values.party_address,
  party_time: values.party_time,
  party_map_url: values.party_map_url,
  gifts_message: values.gifts_message,
  bank_transfer_enabled: values.bank_transfer_enabled,
  bank_account_holder: values.bank_account_holder,
  bank_account_number: values.bank_account_number,
  bank_name: values.bank_name,
  cash_envelope_enabled: values.cash_envelope_enabled,
  gift_registry_url: values.gift_registry_url,
  qr_transfer_enabled: values.qr_transfer_enabled,
  qr_image_url: values.qr_image_url,
  couple_gallery_enabled: values.couple_gallery_enabled,
  guest_photos_enabled: values.guest_photos_enabled,
  children_allowed: values.children_allowed,
  background_music_url: values.background_music_url,
  itinerary: values.itinerary as ItineraryItem[],
  sections: {
    // Campos de sección editados en el editor (fotos, historia.frase, vestimenta…)
    ...(values.sections ?? {}),
    // Familia viene de su propia tabla (invitation_family), no del JSONB.
    familia: familyMembers
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((f) => ({ rol: FAMILY_ROLE_LABELS[f.role as FamilyRole] ?? f.role, nombre: f.name })),
  },
});

const PreviewTab = ({ isPublished, publicSlug, familyMembers }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const values = form.watch() as InvitationFormValues;
  const { data: event } = useEvent();

  // 006: nombres/fecha son del Evento (fuente única); el resto, del formulario.
  const adapterEvent: AdapterEvent = {
    bride_name: event?.bride_name ?? values.bride_name,
    groom_name: event?.groom_name ?? values.groom_name,
    wedding_date: event?.wedding_date ?? values.wedding_date,
    couple_name_order: event?.couple_name_order, // 008 · US10
    rsvp_deadline: event?.rsvp_deadline, // la fecha límite mostrada deriva del evento
  };
  // Resumen: respeta el orden de nombres elegido en "Mi boda" (novia/novio primero).
  const primerNombre =
    adapterEvent.couple_name_order === 'groom_first'
      ? adapterEvent.groom_name || 'Novio'
      : adapterEvent.bride_name || 'Novia';
  const segundoNombre =
    adapterEvent.couple_name_order === 'groom_first'
      ? adapterEvent.bride_name || 'Novia'
      : adapterEvent.groom_name || 'Novio';
  const payload = withPreviewSmoke(
    buildPayload({
      invitation: toAdapterInvitation(values, familyMembers),
      event: adapterEvent,
    }),
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-accent/10">
        <CardTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Vista Previa de tu Invitación
        </CardTitle>
        <CardDescription>
          Así se verá tu invitación con el estilo y los datos actuales.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/50 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Heart className="w-4 h-4 text-primary" />
              {primerNombre} & {segundoNombre}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {parseCalendarDate(adapterEvent.wedding_date)?.toLocaleDateString('es-ES', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }) ?? 'Sin fecha'}
            </span>
          </div>
          {isPublished && publicSlug && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/i/${publicSlug}`} target="_blank" rel="noopener">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver invitación pública
              </a>
            </Button>
          )}
        </div>
        <ScrollArea className="h-[70vh]">
          <InvitationV2 payload={payload} open />
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default PreviewTab;
